import React, { useEffect, useState } from 'react';
import { ReactComponent as ArrowDownIcon } from 'assets/icons/Arrow-down.svg';
import { getSimpleDateFormat, handleApiError, notificationDateRange } from 'utils/functions';
import useDashboardContext from 'contexts/dashboard.context';
import { apiService } from 'services/base.service';
import InfiniteScroll from 'react-infinite-scroll-component';
import SortInput from 'components/base/sort-input';
import { ENUM_NOTIFICATION_FILTERS } from 'utils/enum';
import './styles.scss';
import { NotificationsType } from 'models/notification.model';
import { SideModal } from '@cokitchen/cokitchen-components';

type NotificationProps = {
  show: boolean;
  onClose: () => void;
};

const NotificationSideModal = ({ show, onClose }: NotificationProps) => {
  const defaultRange = ENUM_NOTIFICATION_FILTERS.THIS_MONTH;
  const { activeCompany } = useDashboardContext();
  const [notificationData, setNotificationData] = useState<NotificationsType[]>([]);
  const companyId: string = activeCompany?.id;
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [filterNotifications, setFilterNotifications] = useState<any>(defaultRange);

  const [startDate, setStartDate] = useState(notificationDateRange(defaultRange).startDate);
  const [endDate, setEndDate] = useState(notificationDateRange(defaultRange).endDate);

  const prefillStartEndDate = () => {
    const { startDate: start, endDate: end } = notificationDateRange(filterNotifications);
    setStartDate(start);
    setEndDate(end);
  };

  useEffect(() => {
    prefillStartEndDate();
  }, [filterNotifications]);

  const options = [
    { name: ENUM_NOTIFICATION_FILTERS.TODAY },
    { name: ENUM_NOTIFICATION_FILTERS.THIS_WEEK },
    { name: ENUM_NOTIFICATION_FILTERS.LAST_WEEK },
    { name: ENUM_NOTIFICATION_FILTERS.THIS_MONTH }
  ];

  const fetchNotifications = async (isNew?: boolean) => {
    if (!companyId) return;
    if (isNew) setLoading(true);
    try {
      const res = await apiService.get(`company/notification`, {
        params: {
          start_date: startDate,
          end_date: endDate,
          company_id: companyId,
          page: isNew ? 1 : page
        }
      });
      const { data } = res.data;
      setTotalPages(data.total);
      setNotificationData(isNew ? data.results : [...notificationData, ...data.results]);
    } catch (err) {
      handleApiError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications(true);
  }, [companyId, startDate, endDate]);

  useEffect(() => {
    if (page > 1) {
      fetchNotifications();
    }
  }, [page]);

  const fetchMoreData = () => {
    setPage((p) => p + 1);
  };

  const readNotifications = (notificationId: string) => {
    const data = {
      id: notificationId
    };
    const updatedNotificationData = notificationData?.map((notification) =>
      notification.id === notificationId ? { ...notification, is_read: true } : notification
    );
    setNotificationData(updatedNotificationData);
    try {
      apiService.put('company/notification', { data });
    } catch (err) {
      handleApiError(err);
    }
  };

  return (
    <SideModal
      closeBtnVariant='side'
      bodyClassName='w-[98vw] sm:w-[450px]'
      showTopNavbar
      title={
        <div className='flex justify-between mb-2 gap-6'>
          <div>
            <p className='text-[20px] text-[#2E3031] font-medium'>Notifications</p>
            <p className='text-[#6F7376] text-xs mt-2'>Recent updates</p>
          </div>
          <SortInput
            options={options}
            text='Filter'
            filterValue={filterNotifications}
            setFilterValue={setFilterNotifications}
          />
        </div>
      }
      isOpen={show}
      onRequestClose={() => {
        onClose();
        setPage(1);
      }}>
      <div className='mb-8'>
        {loading ? (
          <div className=' text-sm text-center my-8'>Loading ...</div>
        ) : notificationData?.length === 0 ? (
          <div className=' text-sm text-center my-8'>No notifications available</div>
        ) : (
          <InfiniteScroll
            className='infinite_scroll_div'
            loader={
              <p className='text-sm flex justify-center mt-8'>Loading more notifications...</p>
            }
            dataLength={notificationData?.length ?? 0}
            next={() => {
              fetchMoreData();
            }}
            height={520}
            hasMore={totalPages > (notificationData ? notificationData?.length : 0)}
            endMessage={
              <p style={{ textAlign: 'center' }} className='text-sm'>
                <b>Yay! You have seen it all</b>
              </p>
            }>
            {notificationData?.map((item, index: number) => (
              <div
                key={index}
                onClick={() => {
                  readNotifications(item.id);
                }}
                className='flex justify-between w-full items-center gap-3 py-3 cursor-pointer '>
                <div className='flex gap-3'>
                  <div
                    className='bg-[#F4F1F1] min-w-[27px] h-[27px] rounded-full flex justify-center 
                  items-center'>
                    <ArrowDownIcon />
                  </div>
                  <div className='flex flex-col gap-1'>
                    <p className='text-[#595959] text-sm'>{item.message}</p>
                    <p className='text-[#737373] text-[10px]'>
                      {getSimpleDateFormat(item.created_at)}
                    </p>
                  </div>
                </div>
                <div
                  className={`min-w-[10px] h-[10px] bg-[#E60A2B] rounded-full ${
                    item.is_read ? 'bg-[#EDEDEE]' : 'bg-[#E60A2B]'
                  } `}></div>
              </div>
            ))}
          </InfiniteScroll>
        )}
      </div>
    </SideModal>
  );
};

export default NotificationSideModal;
